import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import get from "lodash/get";
import React from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { IDynamicFieldProps, IFormSchemaField } from "../../types";
import {
  buildGroupRowDefaults,
  fieldTypeDefaultValue
} from "../../utils/catalogToInitialValues";
import { isItemFieldVisibleInRow } from "../../utils/isItemFieldVisibleInRow";
import { DynamicFormFieldRenderer } from "../DynamicFormFieldRenderer";

type RowProps = {
  groupField: IFormSchemaField;
  groupName: string;
  index: number;
  /** The row's live values — watched by the parent on the GROUP name (RHF v6
   * useWatch only re-renders for the exact name or the field-array parent, so
   * a per-row `group[i]` subscription would never fire on leaf changes). */
  rowValues: Record<string, unknown>;
  disabled?: boolean;
  onRemove: () => void;
};

// One repeating row: a gray panel titled "{itemLabel} {n}" with a Remove
// action and the row's item fields. Item-field `visibleWhen` (Equals/NotEquals
// only — engine-asserted) is evaluated LOCALLY against the row's own values;
// when the discriminator changes, the now-hidden siblings unmount and RHF v6
// (`shouldUnregister: true`) drops their values from the payload/draft, so a
// row never submits a stale hidden value.
const DynamicFieldGroupRow: React.FC<RowProps> = ({
  groupField,
  groupName,
  index,
  rowValues,
  disabled,
  onRemove
}) => {
  const rowName = `${groupName}[${index}]`;
  const itemFields = groupField.item?.fields ?? [];
  const itemLabel = groupField.item?.itemLabel ?? groupField.label;

  return (
    <WSElement colorBackground="gray50" p="M">
      {/* "L" between row fields, matching the section field gap, so each
          label/input/note cluster inside the row reads as one unit. */}
      <WSList gap="L">
        <WSFlexBox justify="space-between" alignItems="center" wrap="nowrap">
          <WSText weight="medium" color="gray700">
            {itemLabel} {index + 1}
          </WSText>
          <WSButton.Link
            type="button"
            size="S"
            icon="trash"
            destructive
            disabled={disabled}
            onClick={onRemove}
          >
            Remove
          </WSButton.Link>
        </WSFlexBox>
        {itemFields
          .filter(itemField => isItemFieldVisibleInRow(itemField, rowValues))
          .map(itemField => (
            <DynamicFormFieldRenderer
              key={itemField.key}
              field={itemField}
              name={`${rowName}.${itemField.key}`}
              disabled={disabled}
              // RHF v6 Controllers in a field array read their initial value
              // from this prop (appended rows have no defaultValues entry).
              // Re-shown siblings were unregistered while hidden, so they
              // re-seed from the row's live value or the type default.
              defaultValue={
                rowValues[itemField.key] ?? fieldTypeDefaultValue(itemField)
              }
            />
          ))}
      </WSList>
    </WSElement>
  );
};

/**
 * Repeating group renderer (`FormFieldType.Group`): RHF v6 `useFieldArray`
 * rows + an outlined add button. The add button disables when the server
 * resolved `operations.disabled` (e.g. an income cap — the reason arrives as a
 * conditional boxed note) or when `maxItems` is reached. Group-level errors
 * (min/maxItems, server row rejections on the group key) render underneath.
 */
export const DynamicFieldGroup: React.FC<IDynamicFieldProps> = ({
  field,
  name,
  disabled
}) => {
  const { control, errors } = useFormContext();
  const {
    fields: rows,
    append,
    remove
  } = useFieldArray({
    control,
    name
  });
  // Live row values for row-local visibility — watched on the group name (see
  // RowProps.rowValues for why per-row subscriptions don't work in RHF v6).
  const groupValues = (useWatch({ control, name }) ?? []) as Array<
    Record<string, unknown>
  >;
  const item = field.item;
  if (!item) return null;

  const addLabel = item.addLabel ?? `Add ${field.label.toLowerCase()}`;
  const isMaxReached =
    item.maxItems !== undefined && rows.length >= item.maxItems;

  // Group-scoped error (minItems/maxItems or a server error on the group key).
  // Row errors live at `group.i.key` and surface inline on their own inputs.
  const groupError = get(errors, name) as
    | { message?: string }
    | Array<unknown>
    | undefined;
  const groupErrorMessage =
    groupError && !Array.isArray(groupError) ? groupError.message : undefined;

  return (
    <WSList gap="M">
      {rows.map((row, index) => (
        <DynamicFieldGroupRow
          key={row.id}
          groupField={field}
          groupName={name}
          index={index}
          rowValues={groupValues[index] ?? {}}
          disabled={disabled}
          onRemove={() => remove(index)}
        />
      ))}
      <WSButton.Secondary
        type="button"
        icon="plus"
        fullWidth
        disabled={disabled || isMaxReached}
        onClick={() => append(buildGroupRowDefaults(item.fields))}
      >
        {addLabel}
      </WSButton.Secondary>
      {groupErrorMessage ? (
        <WSText.ParagraphSm color="red500">
          {groupErrorMessage}
        </WSText.ParagraphSm>
      ) : null}
    </WSList>
  );
};
